import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Truck,
  FileText,
  Download,
  Upload,
  Search,
  Calendar,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import { getCurrentUser } from "@/lib/auth";

interface TransportationRecord {
  id: number;
  visitId: number;
  plateNumber: string;
  driverName: string;
  transporterName: string;
  orderExternalId?: string;
  product?: string;
  clientOrderNumber?: string;
  actualCheckedInTime?: string;
  actualExitTime?: string;
  timeInLot?: number;
  weighbridgeEntryWeight?: number;
  weighbridgeExitWeight?: number;
  netWeight?: number;
  isNonMatched: boolean;
  accessDecision: string;
  migratedAt: string;
  originalCreatedAt: string;
}

const TransportationPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTransporter, setFilterTransporter] = useState("all");
  const currentUser = getCurrentUser();

  if (currentUser?.role === "port") {
    return (
      <div className="container mx-auto p-6">
        <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
          <div className="text-center py-12">
            <Truck className="h-12 w-12 mx-auto mb-4 text-[hsl(215,20%,45%)]" />
            <h3 className="text-lg font-semibold mb-2 text-white">
              Registry Has Not Been Implemented
            </h3>
            <p className="text-muted-foreground">Will be available soon!</p>
          </div>
        </Card>
      </div>
    );
  }

  const {
    data: transportationData = [],
    isLoading,
    error,
    refetch,
  } = useQuery<TransportationRecord[]>({
    queryKey: ["/api/transportation"],
    queryFn: async () => {
      const response = await fetch("/api/transportation", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          "X-Tenant-Id": localStorage.getItem("tenant_id") || "",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch transportation data");
      }

      return response.json();
    },
    refetchInterval: 30000,
  });

  // Calculate statistics
  const totalRecords = transportationData.length;
  const activeVehicles = new Set(transportationData.map((r) => r.plateNumber))
    .size;
  const completedToday = transportationData.filter((r) => {
    if (!r.actualExitTime) return false;
    const exitDate = new Date(r.actualExitTime).toDateString();
    const today = new Date().toDateString();
    return exitDate === today;
  }).length;
  const inLot = transportationData.filter((r) => !r.actualExitTime).length;

  const transporters = useMemo(
    () =>
      Array.from(
        new Set(
          transportationData
            .map((r) => r.transporterName)
            .filter(Boolean) as string[]
        )
      ),
    [transportationData]
  );

  const filteredRecords = useMemo(() => {
    return transportationData.filter((record) => {
      const matchesSearch =
        record.plateNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.driverName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (record.transporterName &&
          record.transporterName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()));

      const matchesStatus =
        filterStatus === "all" || record.accessDecision === filterStatus;
      const matchesTransporter =
        filterTransporter === "all" ||
        record.transporterName === filterTransporter;

      return matchesSearch && matchesStatus && matchesTransporter;
    });
  }, [searchQuery, filterStatus, filterTransporter, transportationData]);

  const formatTime = (timeString?: string) => {
    if (!timeString) return "N/A";
    return format(new Date(timeString), "HH:mm");
  };

  const formatDate = (timeString?: string) => {
    if (!timeString) return "N/A";
    return format(new Date(timeString), "MMM dd, yyyy");
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusBadge = (record: TransportationRecord) => {
    if (record.isNonMatched) {
      return (
        <Badge className="bg-orange-500/20 text-orange-400 border-0">
          Non-Matched ANPR
        </Badge>
      );
    }

    switch (record.accessDecision) {
      case "PASS":
        return (
          <Badge className="bg-green-500/20 text-green-400 border-0">
            Passed
          </Badge>
        );
      case "HOLD":
        return (
          <Badge className="bg-red-500/20 text-red-400 border-0">Held</Badge>
        );
      case "MANUAL_REVIEW":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
            Manual Review
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-500/20 text-gray-400 border-0">
            Unknown
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Truck className="h-8 w-8 animate-spin mx-auto mb-4 text-[hsl(187,100%,42%)]" />
          <p className="text-muted-foreground">
            Loading transportation records...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export/Import */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)] flex items-center gap-2">
            <FileText className="w-8 h-8" />
            Transportation Records
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track vehicle movements, freight operations, and driver activities
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
            data-testid="button-export"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
            data-testid="button-import"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] hover-elevate">
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs text-muted-foreground">Total Records</p>
              <FileText className="w-5 h-5 text-[hsl(187,100%,42%)]" />
            </div>
            <div className="text-3xl font-bold text-white">{totalRecords}</div>
          </div>
        </Card>

        <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] hover-elevate">
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs text-muted-foreground">Active Vehicles</p>
              <Truck className="w-5 h-5 text-[hsl(187,100%,42%)]" />
            </div>
            <div className="text-3xl font-bold text-white">{activeVehicles}</div>
          </div>
        </Card>

        <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] hover-elevate">
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs text-muted-foreground">Completed Today</p>
              <Calendar className="w-5 h-5 text-[hsl(24.6,95%,53%)]" />
            </div>
            <div className="text-3xl font-bold text-white">{completedToday}</div>
          </div>
        </Card>

        <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] hover-elevate">
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs text-muted-foreground">In Lot</p>
              <Clock className="w-5 h-5 text-[hsl(264,95%,50%)]" />
            </div>
            <div className="text-3xl font-bold text-white">{inLot}</div>
          </div>
        </Card>
      </div>

      {/* Transportation Records Table */}
      <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
        <div className="p-6">
          <h2 className="text-lg font-bold text-[hsl(187,100%,42%)] mb-6">
            Transportation Records ({filteredRecords.length})
          </h2>

          {/* Search and Filters */}
          <div className="space-y-4 mb-6">
            <div className="flex gap-3 flex-wrap">
              <div className="flex-1 min-w-64 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(215,20%,45%)]" />
                <Input
                  placeholder="Search by plate number, driver name, transporter..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  data-testid="input-search-transportation"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="PASS">Passed</SelectItem>
                  <SelectItem value="HOLD">Held</SelectItem>
                  <SelectItem value="MANUAL_REVIEW">Manual Review</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterTransporter}
                onValueChange={setFilterTransporter}
              >
                <SelectTrigger className="w-40 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Transporter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transporters</SelectItem>
                  {transporters.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Records Table */}
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="h-12 w-12 mx-auto mb-4 text-[hsl(215,20%,45%)]" />
              <p className="text-muted-foreground mb-1">
                No transportation records found
              </p>
              <p className="text-xs text-[hsl(215,20%,45%)]">
                No records available
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(215,28%,18%)]">
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Description
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Vehicle LPN
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Transporter
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Status
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Reference
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Date Processed
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Date Left
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Hours in Lot
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-muted-foreground">
                      Driver Name
                    </th>
                    <th className="text-right py-3 px-3 font-semibold text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr
                      key={record.id}
                      className="border-b border-[hsl(215,28%,18%)] hover:bg-[hsl(215,28%,14%)] transition-colors"
                      data-testid={`row-transportation-${record.id}`}
                    >
                      <td className="py-3 px-3 text-white">
                        {record.product || "—"}
                      </td>
                      <td className="py-3 px-3 font-medium text-white">
                        {record.plateNumber}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {record.transporterName || "—"}
                      </td>
                      <td className="py-3 px-3">
                        {getStatusBadge(record)}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {record.orderExternalId || "—"}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {formatDate(record.actualCheckedInTime)}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {formatTime(record.actualExitTime)}
                      </td>
                      <td className="py-3 px-3 text-white">
                        {formatDuration(record.timeInLot)}
                      </td>
                      <td className="py-3 px-3 text-white">
                        {record.driverName}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`button-view-transportation-${record.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TransportationPage;
